/**
 * Typed calls for every `/v1/onboarding/sessions/*` route. Paths and schemas come from
 * `contracts/onboarding-session.ts`; every response is parsed, so a drifting BFF fails loudly here and not
 * as an `undefined` three components down.
 */
import { httpClient } from '@/features/onboarding/services/http-client';
import {
  GetAdAccountsResponseSchema,
  GetAdAnalysisResponseSchema,
  GetAdEventsResponseSchema,
  GetCompetitorsResponseSchema,
  GetSessionResponseSchema,
  ONBOARDING_SESSION_ROUTES,
  BrandGuideResponseSchema,
  PostQuizResponseSchema,
  PutAdAccountsResponseSchema,
  PutCompetitorsResponseSchema,
  PutSessionResponseSchema,
  type AdPlatform,
  type BrandGuideResponse,
  type BrandProfileInput,
  type GetAdAccountsResponse,
  type GetAdAnalysisResponse,
  type GetAdEventsResponse,
  type GetCompetitorsResponse,
  type GetSessionResponse,
  type PostQuizResponse,
  type PutAdAccountsRequest,
  type PutAdAccountsResponse,
  type PutCompetitorsRequest,
  type PutCompetitorsResponse,
  type PutSessionResponse,
} from '@/contracts/onboarding-session';

type RouteTemplate = (typeof ONBOARDING_SESSION_ROUTES)[keyof typeof ONBOARDING_SESSION_ROUTES];

function route(template: RouteTemplate, params: { id: string; platform?: AdPlatform }): string {
  return template
    .replace(':id', encodeURIComponent(params.id))
    .replace(':platform', encodeURIComponent(params.platform ?? ''));
}

/** Create-or-attach the session and start its jobs. Safe to repeat: the BFF dedupes by session id. */
export async function putSession(sessionId: string, url: string): Promise<PutSessionResponse> {
  const raw = await httpClient.put<unknown>(route(ONBOARDING_SESSION_ROUTES.session, { id: sessionId }), { url });
  return PutSessionResponseSchema.parse(raw);
}

/** The aggregated snapshot. Rejects with an HttpError (404 `session_not_found` after a BFF restart). */
export async function getSession(sessionId: string): Promise<GetSessionResponse> {
  const raw = await httpClient.get<unknown>(route(ONBOARDING_SESSION_ROUTES.session, { id: sessionId }));
  return GetSessionResponseSchema.parse(raw);
}

/** Stores the `BrandProfileInput` baseline. Idempotent. */
export async function postQuiz(sessionId: string, answers: BrandProfileInput): Promise<PostQuizResponse> {
  const raw = await httpClient.post<unknown>(route(ONBOARDING_SESSION_ROUTES.quiz, { id: sessionId }), { answers });
  return PostQuizResponseSchema.parse(raw);
}

export async function getBrandGuide(sessionId: string): Promise<BrandGuideResponse> {
  const raw = await httpClient.get<unknown>(route(ONBOARDING_SESSION_ROUTES.brandGuide, { id: sessionId }));
  return BrandGuideResponseSchema.parse(raw);
}

export async function getAdAccounts(sessionId: string): Promise<GetAdAccountsResponse> {
  const raw = await httpClient.get<unknown>(route(ONBOARDING_SESSION_ROUTES.adAccounts, { id: sessionId }));
  return GetAdAccountsResponseSchema.parse(raw);
}

export async function putAdAccounts(
  sessionId: string,
  platform: AdPlatform,
  body: PutAdAccountsRequest,
): Promise<PutAdAccountsResponse> {
  const raw = await httpClient.put<unknown>(
    route(ONBOARDING_SESSION_ROUTES.adAccountsPlatform, { id: sessionId, platform }),
    body,
  );
  return PutAdAccountsResponseSchema.parse(raw);
}

/** Discovery events after `since` (the previous `nextSince`); omit it to start from the beginning. */
export async function getAdEvents(
  sessionId: string,
  platform: AdPlatform,
  since?: number,
): Promise<GetAdEventsResponse> {
  const base = route(ONBOARDING_SESSION_ROUTES.adEvents, { id: sessionId, platform });
  const raw = await httpClient.get<unknown>(since === undefined ? base : `${base}?since=${since}`);
  return GetAdEventsResponseSchema.parse(raw);
}

/** The brand ad profile of the platform's target account (analyzed X of Y, mix, patterns, top ads). */
export async function getAdAnalysis(sessionId: string, platform: AdPlatform): Promise<GetAdAnalysisResponse> {
  const raw = await httpClient.get<unknown>(route(ONBOARDING_SESSION_ROUTES.adAnalysis, { id: sessionId, platform }));
  return GetAdAnalysisResponseSchema.parse(raw);
}

export async function getCompetitors(sessionId: string): Promise<GetCompetitorsResponse> {
  const raw = await httpClient.get<unknown>(route(ONBOARDING_SESSION_ROUTES.competitors, { id: sessionId }));
  return GetCompetitorsResponseSchema.parse(raw);
}

export async function putCompetitors(
  sessionId: string,
  body: PutCompetitorsRequest,
): Promise<PutCompetitorsResponse> {
  const raw = await httpClient.put<unknown>(route(ONBOARDING_SESSION_ROUTES.competitors, { id: sessionId }), body);
  return PutCompetitorsResponseSchema.parse(raw);
}
